/**
 * VolumeMeter — counter wolumenu testowanych kreacji per sesja.
 * Theriot/Haynes: dyscyplina cyklu. Pokazuje X/Y w tym tygodniu i miesiącu.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, AlertTriangle, Loader2 } from "lucide-react";
import { useAutopilotVolumeStats } from "@/hooks/useAutopilot";

export function VolumeMeter({ sessionId }: { sessionId: string }) {
  const { data, isLoading } = useAutopilotVolumeStats(sessionId);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-4 text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Liczę volume...
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const weekPct = Math.min(100, (data.weekCount / data.weeklyCap) * 100);
  const monthPct = Math.min(100, (data.monthCount / data.monthlyCap) * 100);
  const blocked = !data.canCreateMore;

  return (
    <Card className={blocked ? "border-amber-500/60" : ""}>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4" /> Volume kreacji (Theriot/Haynes dyscyplina)
          {blocked && (
            <Badge variant="outline" className="bg-amber-500/20 text-amber-600 border-amber-500/40">
              <AlertTriangle className="h-3 w-3 mr-1" /> Cap osiągnięty
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Bar label="Ten tydzień" current={data.weekCount} cap={data.weeklyCap} pct={weekPct} />
        <Bar label="Ten miesiąc" current={data.monthCount} cap={data.monthlyCap} pct={monthPct} />
        <p className="text-xs text-muted-foreground">
          {blocked
            ? `Autopilot nie wygeneruje nowych kreacji aż reset cyklu. Powód: ${data.reason}.`
            : `Pozostało ${data.weeklyRemaining}/tyg, ${data.monthlyRemaining}/mc.`}
        </p>
      </CardContent>
    </Card>
  );
}

function Bar({
  label,
  current,
  cap,
  pct,
}: {
  label: string;
  current: number;
  cap: number;
  pct: number;
}) {
  const color = pct >= 100 ? "bg-amber-500" : pct >= 80 ? "bg-yellow-500" : "bg-emerald-500";
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono font-medium">
          {current} / {cap}
        </span>
      </div>
      <div className="h-2 bg-muted rounded overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
